import { ref, readonly, onMounted, onUnmounted } from 'vue'
import { BREAKPOINTS, getScreenType } from '../utils/responsive'

/**
 * 响应式屏幕检测组合式函数
 *
 * 用法：
 *   import { useResponsive } from '@/composables/useResponsive'
 *
 *   // 在 setup() 或 <script setup> 中
 *   const { isMobile, isTablet, isDesktop, isWide, screenType, screenWidth } = useResponsive()
 *
 * 模板中：
 *   <div v-if="isMobile">手机端排版</div>
 *   <div v-else-if="isTablet">平板端排版</div>
 *   <div v-else>桌面端排版</div>
 *
 *   <!-- 或用 class 切换 -->
 *   <div :class="['container', `layout-${screenType}`]">
 *
 * @param {Object} [options]
 * @param {number} [options.debounce=100] - resize 事件防抖延迟(ms)
 * @returns {{ isMobile, isTablet, isDesktop, isWide, screenType, screenWidth }}
 */
export function useResponsive(options = {}) {
  const { debounce: debounceMs = 100 } = options

  const screenWidth = ref(window.innerWidth)
  const screenType = ref(getScreenType())
  const isMobile = ref(screenWidth.value <= BREAKPOINTS.mobile)
  const isTablet = ref(screenWidth.value > BREAKPOINTS.mobile && screenWidth.value <= BREAKPOINTS.tablet)
  const isDesktop = ref(screenWidth.value > BREAKPOINTS.tablet && screenWidth.value <= BREAKPOINTS.desktop)
  const isWide = ref(screenWidth.value >= BREAKPOINTS.wide)

  function update() {
    const w = window.innerWidth
    screenWidth.value = w
    screenType.value = getScreenType(w)
    isMobile.value = w <= BREAKPOINTS.mobile
    isTablet.value = w > BREAKPOINTS.mobile && w <= BREAKPOINTS.tablet
    isDesktop.value = w > BREAKPOINTS.tablet && w <= BREAKPOINTS.desktop
    isWide.value = w >= BREAKPOINTS.wide
  }

  let timer = null
  function onResize() {
    if (timer) clearTimeout(timer)
    timer = setTimeout(update, debounceMs)
  }

  onMounted(() => {
    window.addEventListener('resize', onResize)
    // 也监听 orientationchange（移动端横竖屏切换）
    window.addEventListener('orientationchange', () => {
      // orientationchange 后 innerWidth 可能延迟更新，等一帧
      setTimeout(update, 150)
    })
  })

  onUnmounted(() => {
    window.removeEventListener('resize', onResize)
    if (timer) clearTimeout(timer)
  })

  return {
    /** 是否手机端 (<= 767px) */
    isMobile: readonly(isMobile),
    /** 是否平板端 (768 ~ 1023px) */
    isTablet: readonly(isTablet),
    /** 是否普通桌面 (1024 ~ 1439px) */
    isDesktop: readonly(isDesktop),
    /** 是否宽屏 (>= 1440px) */
    isWide: readonly(isWide),
    /** 当前屏幕类型: 'mobile' | 'tablet' | 'desktop' | 'wide' */
    screenType: readonly(screenType),
    /** 当前屏幕宽度 (px) */
    screenWidth: readonly(screenWidth)
  }
}

/**
 * Options API mixin 版本（兼容现有的 Options API 组件）
 *
 * 用法：
 *   import { responsiveMixin } from '@/composables/useResponsive'
 *   export default {
 *     mixins: [responsiveMixin],
 *     // 模板中可直接使用 this.isMobile / this.screenType 等
 *   }
 */
export const responsiveMixin = {
  data() {
    const w = window.innerWidth
    const type = getScreenType(w)
    return {
      screenWidth: w,
      screenType: type,
      isMobile: w <= BREAKPOINTS.mobile,
      isTablet: w > BREAKPOINTS.mobile && w <= BREAKPOINTS.tablet,
      isDesktop: w > BREAKPOINTS.tablet && w <= BREAKPOINTS.desktop,
      isWide: w >= BREAKPOINTS.wide
    }
  },
  mounted() {
    this._responsiveTimer = null
    this._responsiveHandler = () => {
      if (this._responsiveTimer) clearTimeout(this._responsiveTimer)
      this._responsiveTimer = setTimeout(() => {
        const w = window.innerWidth
        this.screenWidth = w
        this.screenType = getScreenType(w)
        this.isMobile = w <= BREAKPOINTS.mobile
        this.isTablet = w > BREAKPOINTS.mobile && w <= BREAKPOINTS.tablet
        this.isDesktop = w > BREAKPOINTS.tablet && w <= BREAKPOINTS.desktop
        this.isWide = w >= BREAKPOINTS.wide
      }, 100)
    }
    window.addEventListener('resize', this._responsiveHandler)
    window.addEventListener('orientationchange', () => {
      setTimeout(this._responsiveHandler, 150)
    })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this._responsiveHandler)
    if (this._responsiveTimer) clearTimeout(this._responsiveTimer)
  }
}
